import {
  RouteRecordRaw,
  createRouter,
  createWebHistory,
  RouteMeta,
  useRoute,
  RouteLocationNormalizedLoaded,
  NavigationFailureType,
  RouteLocationNormalizedGeneric,
  RouteLocationNormalizedLoadedGeneric,
  RouteLocationRaw,
  isNavigationFailure,
  Router,
  RouterView,
  START_LOCATION,
} from "vue-router";
import { releaseSharedDateFilterLatch } from "@/views/Dashboard/sharedDateFilter";
import { DateTime } from "luxon";
import { useDataFiltersStore, applyShareParam } from "@/store/data_filters";
import { recordNavigationTarget } from "@/utils/staleChunk";
import { authRoutes } from "@/router/auth_routes";
import { publicDashboardRoute } from "@/router/organization_settings_routes";
import {
  dashboardLegacyRoutes,
  publicReportRoute,
} from "@/router/reporting_routes";
import { watch, computed, watchEffect } from "vue";
import { hasAccessTier, hasRequiredUserRole } from "@/utils/tiers";
import { publicBudgetRoute } from "@/router/sales_routes";
import { useOrganizationQuery } from "@/services/organization";
import { useFeatureFlags, type FeatureFlags } from "@/services/feature_flags";
import type { FetchStatus, QueryStatus } from "@tanstack/vue-query";
import { useProfileQuery } from "@/services/user";
import { getLighthouseReportViewerLink } from "@/services/lighthouse";
import { useAuthStore } from "@/store/auth";
import { getSessionStatus } from "@/services/auth";
import { useRouteAccess } from "@/utils/routes";
import { backendEmailRoutes } from "@/router/backend_email_routes";
import { feedRoutes, navbarRoutes } from "@/router/sidenav_routes";
import { validateRouteRecords } from "@/router/routeContract";
import {
  agencyHubRoute,
  createOrganizationRoute,
  setupProgressRoute,
} from "@/router/setting_routes";

export { feedRoutes, homeRoutes } from "@/router/sidenav_routes";
export {
  agencyHubRoute,
  createOrganizationRoute,
  profileRoute,
  settingsRoute,
} from "@/router/setting_routes";
function devReferenceRoutes(): RouteRecordRaw[] {
  if (import.meta.env.MODE === "production") {
    return [];
  }
  return [
    {
      path: "icons",
      name: "icons",
      meta: { activityArea: null, title: "Icons" },
      component: () => import("@/views/DevReferencePages/IconList.vue"),
    },
    {
      path: "sink",
      name: "sink",
      meta: { activityArea: null, title: "Sink" },
      component: () => import("@/views/DevReferencePages/Sink.vue"),
    },
    {
      path: "lightbox",
      name: "lightbox_dev",
      meta: { activityArea: null, title: "Lightbox" },
      component: () => import("@/views/DevReferencePages/LightboxTest.vue"),
    },
    {
      path: "sandbox",
      name: "sandbox",
      meta: { activityArea: null, title: "Sandbox" },
      component: () => import("@/views/DevReferencePages/Sandbox.vue"),
      children: [
        {
          path: "utm-builder",
          name: "sandbox_utm_builder",
          meta: { title: "UTM builder alternatives" },
          component: () =>
            import("@/views/DevReferencePages/Sandbox/UtmBuilder/UtmBuilderSandbox.vue"),
        },
        {
          path: "feedback-survey",
          name: "sandbox_feedback_survey",
          meta: { title: "Feedback survey" },
          component: () =>
            import("@/views/DevReferencePages/Sandbox/FeedbackSurveySandbox.vue"),
        },
        {
          path: "open_graph_proxy",
          name: "open_graph_proxy",
          meta: { title: "Open Graph Proxy" },
          component: () =>
            import("@/views/DevReferencePages/Sandbox/OpenGraphProxy.vue"),
        },
        {
          path: "custom_data_import_available_entities",
          name: "custom_data_import_available_entities",
          meta: { title: "Custom Data Import - Available Entities" },
          component: () =>
            import("@/views/DevReferencePages/Sandbox/CustomDataImport/AvailableEntities.vue"),
        },
        {
          path: "custom_data_import_schemas",
          name: "custom_data_import_schemas",
          meta: { title: "Custom Data Import - Schemas" },
          component: () =>
            import("@/views/DevReferencePages/Sandbox/CustomDataImport/Schemas.vue"),
        },
      ],
    },
  ];
}

const oldRedirectRoutes: RouteRecordRaw[] = [
  {
    path: "profile",
    redirect: { name: "profile" },
  },
  {
    path: "marketing-performance/details",
    redirect: { name: "ad-performance-base" },
  },
  {
    path: "website-optimization",
    redirect: { name: "page-insights-search" },
    children: [
      {
        path: ":pk",
        redirect: { name: "page-insights-content" },
      },
    ],
  },
  {
    path: "organic-traffic",
    redirect: { name: "search-term-report" },
    children: [
      {
        path: ":id",
        redirect: { name: "search-term-detail" },
      },
    ],
  },
  {
    path: "/competitor-monitor/competitor-overview",
    redirect: { name: "competitor-overview" },
  },
  {
    path: "/competitor-monitor/competitor-posts",
    redirect: { name: "competitor-posts" },
  },
  {
    path: "website-performance",
    redirect: { name: "website-analytics" },
  },
  {
    path: "website-traffic",
    redirect: { name: "website-analytics" },
  },
  {
    path: "site-score/:pk",
    component: {
      setup() {
        const route = useRoute();
        window.location.replace(
          getLighthouseReportViewerLink(Number(route.params.pk)),
        );
      },
    },
  },
  {
    path: "performance-indicators",
    redirect: { name: "goals" },
  },
  {
    path: "ecommerce",
    redirect: { name: "commerce_ecom" },
  },
  {
    path: "/settings/resources",
    redirect: { name: "resources" },
  },
  {
    path: "/settings/merge-resources",
    redirect: { name: "resources" },
  },
  {
    path: "/settings/merge-resources/edit/:pk",
    redirect: { name: "resources" },
  },
  {
    path: "/settings/add-resource",
    redirect: { name: "add-resource" },
  },
  {
    path: "/settings/add-resource/select-channel",
    redirect: { name: "select-channel" },
  },
  {
    path: "/settings/add-resource/xandr-auth",
    redirect: { name: "xandr-auth" },
  },
  {
    path: "marketing-performance",
    redirect: { name: "ad-performance-base" },
  },
  {
    path: "marketing-performance/overview",
    redirect: { name: "ad-performance-overview" },
  },
  {
    path: "marketing-performance/ads",
    redirect: { name: "marketing-performance-ads" },
  },
  {
    path: "content-lab",
    redirect: { name: "create-content" },
  },
  {
    path: "content-lab/content",
    redirect: { name: "content" },
  },
];

export const appDocsRoutes: RouteRecordRaw[] = [
  {
    path: "/docs",
    alias: ["/privacy"], // Preserved and redirects to keep existing links out in the wild working
    name: "docs",
    meta: {
      activityArea: null,
      title: "Documentation",
      requiresAuth: false,
      shell: "minimal",
      navbar: "authenticated",
      sidepanel: "hidden",
      contentWidth: "standard",
      breadcrumb: false,
    },
    component: () => import("@/views/AppDocs/AppDocs.vue"),
    children: [
      {
        path: ":document",
        name: "docs-document",
        meta: { title: "Documentation" },
        component: () => import("@/views/AppDocs/AppDocs.vue"),
      },
    ],
  },
];

// !!! IMPORTANT !!!
// Remember to change redirect URLs in the deployment repo envs if changing the root URL
const authenticatedExtraRoutes: RouteRecordRaw[] = [
  ...dashboardLegacyRoutes,
  ...oldRedirectRoutes,
  ...devReferenceRoutes(),
  setupProgressRoute,
  agencyHubRoute,
  {
    path: "notifications",
    name: "notifications",
    meta: { activityArea: null, title: "Notifications" },
    component: () => import("@/views/Notifications.vue"),
  },
  createOrganizationRoute,
].map(
  (route): RouteRecordRaw => ({
    ...route,
    meta: { ...(route.meta as RouteMeta), requiresAuth: true } as RouteMeta,
  }),
);

const authedRoutes: RouteRecordRaw[] = [
  ...navbarRoutes,
  ...feedRoutes,
  ...authenticatedExtraRoutes,
];

export const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "base",
    meta: {
      title: "Fyr",
      shell: "application",
      navbar: "persistent",
      sidepanel: "global",
      contentWidth: "standard",
      breadcrumb: false,
    },
    component: RouterView,
    children: [
      ...publicReportRoute,
      ...publicDashboardRoute,
      ...publicBudgetRoute,
      ...authRoutes.map((route) => ({
        ...route,
        meta: {
          ...(route.meta as RouteMeta),
          shell: "none",
          navbar: "hidden",
          sidepanel: "hidden",
          breadcrumb: false,
        } as RouteMeta,
      })),
      ...authedRoutes,
      ...appDocsRoutes,
      {
        path: ":pathMatch(.*)*",
        name: "not-found",
        props: (route) => ({
          resourceRequested: route.query.resourceRequested,
        }),
        meta: {
          activityArea: null,
          title: "404 Not Found",
          shell: "minimal",
          navbar: "authenticated",
          sidepanel: "hidden",
          contentWidth: "standard",
          breadcrumb: false,
        },
        component: () => import("@/views/NotFound.vue"),
      },
      ...backendEmailRoutes(),
    ],
  },
];
validateRouteRecords(routes);

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return { el: to.hash, behavior: "smooth" };
    }
    if (savedPosition) {
      return savedPosition;
    } else {
      // If the route name is the same, we only changed a parameter,
      // in which case we don't want to scroll to the top.
      if (from.name !== to.name) {
        return { top: 0 };
      } else {
        // But in marketing performance the same route is used for different "pages",
        // the only difference is the query parameter `level`.
        if (from.query.level !== to.query.level) {
          return { top: 0 };
        }
      }
    }
  },
});

router.beforeEach(releaseSharedDateFilterLatch);

// A stored homepage preference is only usable if it can actually be navigated
// to by name alone. `router.hasRoute()` is not enough: a route with path params
// (e.g. `budget_tool_detail`) is "known" but resolving it without an id throws
// `Missing required param`, which left users who had saved such a preference —
// the picker offered them until #8072 — unable to load any page, including the
// settings screen where they could change it back.
// Router-injectable so tests can drive the real logic against their own
// memory router instead of reimplementing it.
export function resolvableRouteName(
  r: Router,
  homeRoute: string | null | undefined,
): string | null {
  // Empty string must be rejected up front: `resolve({ name: "" })` does not
  // throw, it resolves to "/" — which routes straight back into this guard.
  if (!homeRoute) return null;
  try {
    // Resolve against START_LOCATION, not the current route: vue-router
    // inherits matching required params from wherever the user happens to be,
    // so on /budget-tool/123 a bare resolve would accept
    // `budget_tool_detail` and send a Home redirect back to that same plan.
    // Throws both for an unknown name and for a known route whose path needs
    // params, which is the pair of failures a stored preference can hit.
    r.resolve({ name: homeRoute }, START_LOCATION);
    return homeRoute;
  } catch {
    return null;
  }
}

export function resolvableHomeRouteName(
  homeRoute: string | null | undefined,
): string | null {
  return resolvableRouteName(router, homeRoute);
}

const DEFAULT_HOME_ROUTE_NAME = "home";

// The guard's home-route decision, kept apart from its auth and profile-loading
// plumbing so tests can drive it against their own router.
export function homeRouteTargetName(
  r: Router,
  profile: { isError?: boolean; selectedHomeRoute?: string | null },
): string {
  if (profile.isError) return DEFAULT_HOME_ROUTE_NAME;
  // An explicit preference wins, including "hub": the picker only offers it to
  // agency members and the /hub guard re-checks access.
  return (
    resolvableRouteName(r, profile.selectedHomeRoute) ?? DEFAULT_HOME_ROUTE_NAME
  );
}

// For the Hub's own exits: leaving the Hub must never land back on it, so a
// Hub preference falls through to Home.
export function homeRouteNameAwayFromHub(
  r: Router,
  storedHomeRoute: string | null | undefined,
): string {
  const homeRoute = resolvableRouteName(r, storedHomeRoute);
  return homeRoute && homeRoute !== "hub" ? homeRoute : DEFAULT_HOME_ROUTE_NAME;
}

// Every redirect to login must carry the reason: after a forced logout the
// still-mounted view can trigger one more navigation, and the last redirect
// to win decides what the login page can explain.
export function loginRedirect(
  authStore: ReturnType<typeof useAuthStore>,
  to: { fullPath: string },
): RouteLocationRaw {
  const reason = authStore.currentLogoutReason();
  return {
    name: "login",
    query: {
      redirect: to.fullPath,
      ...(reason ? { reason } : {}),
    },
  };
}

// "base" is the root entry URL. It carries no requiresAuth of its own — it is
// the shared parent of login and the public-sharing routes — but the home guard
// sends an authed visitor straight on to a protected view, and only after
// awaiting the profile request and its retries. Verify the session here instead.
function needsSessionVerification(to: RouteLocationNormalizedGeneric): boolean {
  return (
    to.matched.some((record) => record.meta.requiresAuth) || to.name === "base"
  );
}

export function useRequiresAuthNavigationGuard(): void {
  const authStore = useAuthStore();
  // Vue Router acts on a returned redirect before it checks whether the
  // navigation was superseded, so a probe outliving its navigation would drag
  // an already-open public page to login.
  let latestNavigation = 0;
  // Pushing the URL already on screen cancels a pending navigation but reports
  // it as duplicated without running beforeEach, so that one cancellation has
  // to invalidate the probe from here. Only that one: an older canceled
  // navigation can settle after a newer one entered beforeEach, and counting it
  // would discard the probe the current navigation is still waiting on.
  router.afterEach((_to, _from, failure) => {
    if (isNavigationFailure(failure, NavigationFailureType.duplicated)) {
      latestNavigation += 1;
    }
  });
  router.beforeEach(async (to) => {
    const navigation = ++latestNavigation;
    if (!needsSessionVerification(to)) return;
    if (!authStore.isAuthed) return loginRedirect(authStore, to);
    if (!authStore.sessionNeedsVerification()) return;

    // A tab left open past the session's lifetime still has isAuthed=true.
    // Probing here sends it to login at once, instead of rendering the view
    // and waiting for its data requests to come back 403.
    const revisionAtProbe = authStore.authRevision;
    const isAuthenticated = await getSessionStatus();
    if (navigation !== latestNavigation) return;
    // A login or logout (here or in another tab) while the probe was in flight
    // makes its answer stale; acting on it could undo the newer login.
    if (authStore.authRevisionMovedSince(revisionAtProbe)) {
      // Re-read rather than trusting the pre-await sample; a cross-tab logout
      // may have landed while the probe was in flight.
      return authStore.isAuthed ? undefined : loginRedirect(authStore, to);
    }
    if (isAuthenticated === null) {
      // The 403 interceptor still catches a dead session meanwhile.
      authStore.markProbeFailed();
      return;
    }
    if (isAuthenticated) {
      authStore.markSessionVerified();
      return;
    }
    authStore.setLoggedOut("session_expired");
    return loginRedirect(authStore, to);
  });
}

export function useHomeRouteRedirectGuard(): void {
  const authStore = useAuthStore();
  const profile = useProfileQuery();

  router.beforeEach(async (to) => {
    // Block non-agency users from accessing the hub route before the view loads
    if (to.name === "hub" && authStore.isAuthed) {
      if (profile.isLoading) {
        await new Promise<void>((resolve) => {
          const unwatch = watch(
            () => profile.isLoading,
            (loading) => {
              if (!loading) {
                unwatch();
                resolve();
              }
            },
            { immediate: true },
          );
        });
      }

      // If query errored, allow navigation (hub view will handle error state)
      if (profile.isError) {
        return;
      }

      // Hub access follows hasAgencyHub — true when the user has at least one
      // flag-enabled member agency, matching the backend (which serves the hub
      // if ANY member agency is enabled, not only the home agency). Selection-
      // independent, so /hub stays reachable from any org.
      if (!profile.data?.hasAgencyHub) {
        return { name: "home" };
      }
    }

    // Handle navigation to base route
    if (to.name === "base") {
      if (!authStore.isAuthed) return loginRedirect(authStore, to);

      // Wait for profile data to load
      if (profile.isLoading) {
        await new Promise<void>((resolve) => {
          const unwatch = watch(
            () => profile.isLoading,
            (loading) => {
              if (!loading) {
                unwatch();
                resolve();
              }
            },
            { immediate: true },
          );
        });
      }

      return {
        name: homeRouteTargetName(router, {
          isError: profile.isError,
          selectedHomeRoute: profile.data?.selectedHomeRoute,
        }),
      };
    }
    return;
  });
}
// Record where each navigation is headed so the stale-chunk recovery can
// reload straight to the target route when its lazy chunk 404s after a deploy,
// instead of reloading the page the user is still on and losing the click.
// Cleared on completion so a later widget/async-import failure reloads in place.
router.beforeEach((to) => {
  recordNavigationTarget(to.fullPath);
});
router.afterEach(() => {
  recordNavigationTarget(null);
});

router.beforeResolve((to) => {
  const shareParam = (to.query.share ?? to.redirectedFrom?.query.share) as
    | string
    | undefined;
  if (shareParam) {
    applyShareParam(shareParam);
  }

  // Clamp any future dates that may have leaked into the global temporal filter
  // (e.g. from dashboard editor allowing future dates for locked date ranges)
  const store = useDataFiltersStore();
  const today = DateTime.local().toISODate()!;
  const hasFutureSelection = store.temporalFilter?.selection?.end > today;
  const hasFutureComparison =
    (store.temporalFilter?.comparison?.end ?? "") > today;
  if (hasFutureSelection || hasFutureComparison) {
    // setTemporalFilter clamps both selection and comparison dates
    store.setTemporalFilter(store.temporalFilter);
  }
});

export default router;

export enum NotFoundErrors {
  ROUTE = "ROUTE",
  INVITE = "INVITE",
  ACCESS_TIER = "ACCESS_TIER",
  OTHER = "OTHER",
}
export function directToNotFound(
  res: NotFoundErrors,
  route: RouteLocationNormalizedLoaded,
): void {
  // https://router.vuejs.org/guide/essentials/dynamic-matching.html#catch-all-404-not-found-route
  router.push({
    name: "not-found",
    // preserve current path and remove the first char to avoid the target URL starting with `//`
    params: { pathMatch: route.path.substring(1).split("/") },
    // preserve existing query and hash if any
    query: { ...route.query, resourceRequested: res },
    hash: route.hash,
  });
}

export function watchRouteForAccessTier(): void {
  // Fires once every route change
  const route = useRoute();
  if (shouldSkipRoute(route)) return;
  const tier = computed(() => {
    return route.meta.requiredTier;
  });
  const organization = useOrganizationQuery();

  watch(
    [() => organization.data, tier],
    () => {
      if (
        tier.value !== undefined &&
        organization.data &&
        !hasAccessTier(organization.data.subscriptionTier, tier.value)
      ) {
        directToNotFound(NotFoundErrors.ACCESS_TIER, route);
      }
    },
    { immediate: true, deep: true },
  );
}
export function watchRouteForUserRole(): void {
  // Fires once every route change
  const route = useRoute();
  if (shouldSkipRoute(route)) return;
  const role = computed(() => {
    return route.meta.requiredUserRole;
  });
  const user = useProfileQuery();

  watch(
    [() => user.data, role],
    () => {
      if (
        role.value !== undefined &&
        user.data &&
        !hasRequiredUserRole(user.data.role, role.value)
      ) {
        directToNotFound(NotFoundErrors.ROUTE, route);
      }
    },
    { immediate: true, deep: true },
  );
}

export function watchRouteForOrganization(): void {
  // Fires once every route change
  const route = useRoute();
  if (shouldSkipRoute(route)) return;
  const routeAccess = useRouteAccess(
    computed(() => route),
    true,
  );

  watchEffect(() => {
    if (!routeAccess.show.value) {
      directToNotFound(NotFoundErrors.ROUTE, route);
    }
  });
}

export function watchRouteForResetFilters(): void {
  const route = useRoute();
  if (shouldSkipRoute(route)) return;
  const dataFiltersStore = useDataFiltersStore();

  watch(
    () => route.meta,
    (newMeta, oldMeta) => {
      if (
        oldMeta &&
        Object.keys(oldMeta).length &&
        (!oldMeta.text || !newMeta.text || oldMeta.text !== newMeta.text)
      ) {
        dataFiltersStore.resetSharableFilters();
      }
    },
    { deep: true, immediate: true },
  );
}

// Exported so tests can drive the real decision instead of re-implementing it.
// `meta` here must be the MERGED `route.meta` of the matched chain, which is
// what makes an inherited `requiredFeatureFlag` from a parent apply to a child.
export function routeIsBlockedByFeatureFlag(
  meta: RouteMeta,
  flags: {
    data?: FeatureFlags;
    status: QueryStatus;
    fetchStatus?: FetchStatus;
  },
): boolean {
  const required = meta.requiredFeatureFlag;
  if (!required) return false;
  // Paywalled routes stay on their own URL and render the pitch in place
  // (PageFrame swaps the view for FeaturePaywall), so bouncing them to 404
  // would hide the thing we're trying to sell. Note this skips only a TRUTHY
  // paywall: `paywall: false` is an opt-out of an ancestor's pitch, not of the
  // flag requirement, so a child that inherits a requirement is still blocked.
  if (meta.paywall) return false;
  // Hold while the query has no answer, then fail CLOSED: a fetch error
  // (data stays undefined) must block the gated route, not leave it open.
  //
  // `status`, not `isLoading`: the query is held until the session's
  // organization is known, and a held query reports `pending` with nothing in
  // flight — `isLoading: false`, no data. Read as a settled response that
  // omits the flag, that 404'd every gated non-paywalled route on a hard load,
  // because this watcher runs before the shell's profile query resolves.
  if (flags.status === "pending") return false;
  // A query parked because the browser is offline reports neither loading nor
  // error. Redirecting to 404 on that would tell an entitled user their page
  // doesn't exist, so the route is held instead — still closed (nothing
  // renders the gated view), but recoverable when the connection returns and
  // the query unpauses.
  if (flags.fetchStatus === "paused") return false;
  return flags.data?.[required]?.enabled !== true;
}

export function watchRouteForFeatureFlag(): void {
  const route = useRoute();
  if (shouldSkipRoute(route)) return;
  const flags = useFeatureFlags();

  watch(
    [
      () => flags.data,
      () => flags.status,
      () => flags.fetchStatus,
      () => route.meta,
    ],
    () => {
      if (routeIsBlockedByFeatureFlag(route.meta, flags)) {
        directToNotFound(NotFoundErrors.ROUTE, route);
      }
    },
    { immediate: true, deep: true },
  );
}

export function watchRouteForPermissionRequirement(): void {
  const route = useRoute();
  if (shouldSkipRoute(route)) return;
  const self = useProfileQuery();

  watch(
    [() => self.data, () => route.meta],
    () => {
      if ("requiredPermissions" in route.meta) {
        if (self.data) {
          for (const permission of route.meta.requiredPermissions ?? []) {
            // `isAgency` means "the user belongs to an agency", which is
            // captured by their server-resolved home agency rather than the
            // profile's own boolean flags or the active org. Mirrors
            // Navbar.vue's filteredSettingsRoutes special-case. The
            // agency_hub flag (greyed nav / feed redirect) is handled by
            // useHomeRouteRedirectGuard, not here — this guard only checks
            // membership.
            if (permission === "isAgency") {
              if (!self.data.homeAgency) {
                directToNotFound(NotFoundErrors.ROUTE, route);
                return;
              }
              continue;
            }
            if (
              permission in self.data &&
              !self.data[permission as keyof typeof self.data]
            ) {
              directToNotFound(NotFoundErrors.ROUTE, route);
              return;
            }
          }
        }
      }
    },
    { immediate: true, deep: true },
  );
}

function shouldSkipRoute(route: RouteLocationNormalizedLoadedGeneric): boolean {
  return (
    route === undefined ||
    route.name === undefined ||
    route.name === "shared-dashboard"
  );
}
